import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATH_INFO } from "../pages/utils/helper";
import { format } from "date-fns";

export type SamplesResponseType = {
  id: string
  part_number: string
  repeat: number
  size: string
  color_no: string
  qr_code: string
  url: string
  kind: 'sample' | 'final' | null
  time: number
  brand_name: string
  item_name: string
  sample_number: string
  created_at: string
  updated_at: string
  sampleMeasurements: sampleMeasurements[]
  sampleWeightMeasurements: sampleWeightMeasurements[]
  sampleImages: sampleImages[]
  sampleEcImages: sampleImages[]
  companyId: string
  clothtype: string
  device_id?: number // FIXME: 暫定対応
  device_name?: string
  device_location?: string
}

export type sampleMeasurements = {
  measure_index: number
  measure_name: string
  image_index: number
  resolution: number
  result_pixel: number,
  result_real: number,
  result_point: { x: number, y: number }[]
  measure_type: 'auto' | 'manual' | ''
  time: number
  is_deleted: boolean
}

type sampleWeightMeasurements = {
  weight: number
}

export type sampleImages = {
  url: string
  thumbnail_url: string
  image_index: number
  sample_id: string
}

export const sampleDefault: SamplesResponseType = {
  id: '',
  part_number: '',
  repeat: 0,
  size: '',
  color_no: '',
  qr_code: '',
  url: '',
  kind: null,
  time: 0,
  brand_name: '',
  item_name: '',
  sample_number: '',
  created_at: '',
  updated_at: '',
  sampleMeasurements: [],
  sampleWeightMeasurements: [],
  sampleImages: [],
  sampleEcImages: [],
  companyId: '',
  clothtype: '',
  device_id: 0, // FIXME: 暫定対応
  device_name: '',
}

export type DiffType = {
  data: {
    condition: { hasSpec: boolean, hasTolerance: boolean }
    points: Points[]
  }
  status: string
}

type Tolerance = {
  minus: number
  plus: number
}

type Points = {
  measure_name: string
  result_real: number
  spec: number
  diff: number
  validate: boolean
  tolerance: Tolerance[]
}

export const diffDefault: DiffType = {
  data: {
    condition: { hasSpec: false, hasTolerance: false },
    points: []
  },
  status: ''
}

const SAMPLES_PAGE_LIMIT = 50

export type SamplesMeta = {
  total: number
  page: number
  limit: number
  hasMore: boolean
  nextPage: number | null
}

type SamplesApiResponse = {
  data: SamplesResponseType[]
  meta: SamplesMeta
}

const defaultSamplesMeta: SamplesMeta = {
  total: 0,
  page: 1,
  limit: SAMPLES_PAGE_LIMIT,
  hasMore: false,
  nextPage: null
}

// New type for filter parameters
export type FilterParams = {
  userName?: string;
  partNumber?: string;
  sampleStatus?: string;
  brandName?: string;
  size?: string;
  colorNo?: string;
  createdFrom?: Date;
  createdTo?: Date;
};

export const SamplesContext = createContext<{
  samples: SamplesResponseType[]
  sample: SamplesResponseType
  rowSample: SamplesResponseType
  diff: DiffType
  canvasImage?: string | null
  samplesMeta: SamplesMeta
  isSamplesLoading: boolean
}>({
  samples: [],
  sample: sampleDefault,
  rowSample: sampleDefault,
  diff: diffDefault,
  canvasImage: null,
  samplesMeta: defaultSamplesMeta,
  isSamplesLoading: false
})

export const UpdateSamplesContext = createContext<{
  updateRowSample: (value: Partial<SamplesResponseType>) => void
  increment: () => void
  decrement: () => void
  refreshSamples: (filters?: FilterParams, options?: { append?: boolean }) => Promise<void>
  loadMoreSamples: () => Promise<void>
  updateSample: (obj: SamplesResponseType) => void
  idSearchSample: (id: string, isAuth: boolean, shouldSetState: boolean) => Promise<SamplesResponseType | void>
  saveSample: (obj: SamplesResponseType, isUpdate: boolean) => Promise<[boolean, number]>
  fetchUrlQrSample: (sampleUrl?: string, file?: File) => Promise<void>
  fetchDiff: (sample: SamplesResponseType) => Promise<void>
  fetchQrCode: (sampleId: string) => Promise<{ id: string, url: string, qr_code: string } | null>
  updateCanvasImage: (imageDataUrl: string) => void
}>({
  updateRowSample: (value: Partial<SamplesResponseType>) => { },
  increment: () => { },
  decrement: () => { },
  refreshSamples: async (filters?: FilterParams) => { },
  loadMoreSamples: async () => { },
  updateSample: (obj: SamplesResponseType) => { },
  idSearchSample: async (id: string, isAuth: boolean, shouldSetState: boolean) => { },
  saveSample: async (obj: SamplesResponseType, isUpdate: boolean) => [false, 0],
  fetchUrlQrSample: async (sampleUrl?: string, file?: File) => { },
  fetchDiff: async (sample: SamplesResponseType) => { },
  fetchQrCode: async (sampleId: string) => null,
  updateCanvasImage: (imageDataUrl: string) => { },
})

export function useSamplesContext() {
  const navigate = useNavigate()
  const [samples, setSamples] = useState<SamplesResponseType[]>([])
  const [sample, setSample] = useState(sampleDefault)
  const [rowSample, setRowSample] = useState(sampleDefault)
  const [diff, setDiff] = useState<DiffType>(diffDefault)
  const [canvasImage, setCanvasImage] = useState<string | null>(null)
  const [samplesMeta, setSamplesMeta] = useState<SamplesMeta>(defaultSamplesMeta)
  const [isSamplesLoading, setIsSamplesLoading] = useState<boolean>(false)
  const samplesMetaRef = useRef<SamplesMeta>(defaultSamplesMeta)
  const activeFiltersRef = useRef<FilterParams | undefined>(undefined)

  useEffect(() => {
    samplesMetaRef.current = samplesMeta
  }, [samplesMeta])

  const updateCanvasImage = useCallback((imageDataUrl: string) => {
    setCanvasImage(imageDataUrl)
  }, [])

  const updateRowSample = useCallback((value: Partial<SamplesResponseType>) => {
    setRowSample((prev) => ({ ...prev, ...value }))
  }, [])

  const increment = useCallback(() => {
    setRowSample((prev) => ({ ...prev, repeat: prev.repeat + 1 }))
  }, [])

  const decrement = useCallback(() => {
    setRowSample((prev) => ({ ...prev, repeat: prev.repeat - 1 }))
  }, [])

  const mergeSamples = useCallback((current: SamplesResponseType[], next: SamplesResponseType[]) => {
    if (current.length === 0) return next
    const existingIds = new Set(current.map((item) => item.id))
    const merged = [...current]
    next.forEach((item) => {
      if (!existingIds.has(item.id)) {
        merged.push(item)
      }
    })
    return merged
  }, [])

  const buildQueryParams = useCallback((filters: FilterParams | undefined, page: number) => {
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('limit', String(SAMPLES_PAGE_LIMIT))

    if (filters?.userName) params.append('user_name', filters.userName)
    if (filters?.partNumber) params.append('part_number', filters.partNumber)
    if (filters?.sampleStatus) params.append('sample_status', filters.sampleStatus)
    if (filters?.brandName) params.append('brand_name', filters.brandName)
    if (filters?.size) params.append('size', filters.size)
    if (filters?.colorNo) params.append('color_no', filters.colorNo)
    if (filters?.createdFrom) params.append('created_from', format(filters.createdFrom, 'yyyy-MM-dd'))
    if (filters?.createdTo) params.append('created_to', format(filters.createdTo, 'yyyy-MM-dd'))

    return params.toString()
  }, [])

  const refreshSamples = useCallback(async (filters?: FilterParams, options?: { append?: boolean }) => {
    const shouldAppend = options?.append ?? false
    const meta = samplesMetaRef.current
    const targetPage = shouldAppend ? (meta.nextPage ?? meta.page + 1) : 1
    if (shouldAppend && !meta.hasMore) {
      return
    }

    const normalizedFilters = !shouldAppend ? (filters ? { ...filters } : undefined) : undefined
    const filtersToUse = shouldAppend ? activeFiltersRef.current : normalizedFilters
    if (!shouldAppend) {
      activeFiltersRef.current = normalizedFilters
    }

    try {
      let url = `${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/department`
      const queryString = buildQueryParams(filtersToUse, targetPage)
      if (queryString) {
        url = `${url}?${queryString}`
      }

      setIsSamplesLoading(true)

      const res = await fetch(url, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      })
      if (res.status === 403) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await res.json() as SamplesApiResponse;
      const items = data.data ?? []
      const nextMeta = data.meta ?? defaultSamplesMeta
      setSamples((prev) => shouldAppend ? mergeSamples(prev, items) : items)
      setSamplesMeta(nextMeta)
    } catch (error) {
      console.error("Error fetching samples.");
    } finally {
      setIsSamplesLoading(false)
    }
  }, [navigate, buildQueryParams, mergeSamples])

  const loadMoreSamples = useCallback(async () => {
    if (isSamplesLoading || !samplesMetaRef.current.hasMore) return
    await refreshSamples(undefined, { append: true })
  }, [isSamplesLoading, refreshSamples])

  const updateSample = useCallback((obj: SamplesResponseType) => {
    if (!obj) return
    setSample(obj)
  }, [])

  const idSearchSample = useCallback(async (id: string, isAuth: boolean, shouldSetState: boolean): Promise<SamplesResponseType | void> => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${id}`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          Authorization: isAuth ? `Bearer ${sessionStorage.getItem('token')}` : ''
        },
      })
      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await res.json();
      if (shouldSetState) {
        setSample(data)
        setRowSample(data)
      } else {
        return data
      }
    } catch (error) {
      console.error("Error fetching sample.");
    }
  }, [navigate])

  const saveSample = useCallback(async (obj: SamplesResponseType, isUpdate: boolean): Promise<[boolean, number]> => {
    const { url, qr_code, sampleImages, sampleEcImages, sampleWeightMeasurements, ...otherData } = obj
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/save/${otherData.id}`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ ...otherData, isUpdate })
      })
      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        return [false, res.status]
      }

      const data = await res.json();
      idSearchSample(data.id, true, true)
      refreshSamples()
      return [true, res.status]
    } catch (error) {
      console.error("Error fetching samples.");
      return [false, 500]
    }
  }, [navigate, refreshSamples, idSearchSample])

  const fetchUrlQrSample = useCallback(async (sampleUrl?: string, file?: File) => {
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      let body: FormData | string
      let options: RequestInit

      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        body = formData
        options = {
          method: 'POST',
          headers,
          body,
        }
      } else if (sampleUrl) {
        headers['Content-Type'] = 'application/json'
        body = JSON.stringify({ sample_url: sampleUrl })
        options = {
          method: 'POST',
          headers,
          body,
        }
      } else {
        throw new Error('ファイルまたはURLいずれかが必要です')
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/qr-code`, options)

      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error('ERROR UPLOAD')
      }

      const data = await res.json();
      setSample(data)
      setRowSample(data)
    } catch (error) {
      console.error('Error fetching sample')
    }
  }, [navigate])

  const fetchDiff = useCallback(async (sample: SamplesResponseType) => {
    const baseUrl = `${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec/diff`
    const url = sample.companyId ? `${baseUrl}/${sample.companyId}` : baseUrl

    const newMeasurements = sample.sampleMeasurements.filter((sm) => !sm.is_deleted)

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({
          part_number: sample.part_number,
          sample_number: sample.sample_number,
          repeat: sample.repeat,
          time: sample.time,
          brand_name: sample.brand_name,
          measurements: newMeasurements,
          size: sample.size,
          kind: sample.kind,
          color_no: sample.color_no || null,
        })
      })

      const data = await res.json()
      setDiff(data)
    } catch (error) {
      console.log('ERROR FETCH')
    }
  }, [])

  const fetchQrCode = useCallback(async (sampleId: string): Promise<{ id: string, url: string, qr_code: string } | null> => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${sampleId}/qr-code`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem('token');
        navigate(PATH_INFO.LOGIN.PATH);
        return null;
      }

      if (!res.ok) {
        throw new Error('QRコードの取得に失敗しました。');
      }
      return await res.json();
    } catch (error) {
      console.error("Error fetching QR");
      return null;
    }
  }, [navigate]);
  return {
    samples,
    sample,
    rowSample,
    diff,
    canvasImage,
    samplesMeta,
    isSamplesLoading,
    updateCanvasImage,
    updateRowSample,
    increment,
    decrement,
    refreshSamples,
    loadMoreSamples,
    updateSample,
    idSearchSample,
    saveSample,
    fetchUrlQrSample,
    fetchDiff,
    fetchQrCode,
  }
}

export function SamplesProvider({ children }: { children: React.ReactNode }) {
  const { samples, sample, rowSample, diff, canvasImage, samplesMeta, isSamplesLoading, updateCanvasImage, updateRowSample, increment, decrement, refreshSamples, loadMoreSamples, updateSample, idSearchSample, saveSample, fetchUrlQrSample, fetchDiff, fetchQrCode } = useSamplesContext()

  return (
    <UpdateSamplesContext.Provider value={{ updateCanvasImage, updateRowSample, increment, decrement, refreshSamples, loadMoreSamples, updateSample, idSearchSample, saveSample, fetchUrlQrSample, fetchDiff, fetchQrCode }}>
      <SamplesContext.Provider value={{ samples, sample, rowSample, diff, canvasImage, samplesMeta, isSamplesLoading }}>
        {children}
      </SamplesContext.Provider>
    </UpdateSamplesContext.Provider>
  )
}